import { GridCellProps } from '@progress/kendo-react-grid'
import { CategoryType, ClientDocumentType } from 'api/Documents/Types/ClientDocument'
import { CountryDto } from 'api/Identity/Types/Country'
import { Option, Select } from 'components/Select'
import { useAppDispatch, useAppSelector } from 'store'
import { getDocumentsTypesByCategory } from 'store/documents/documents.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { DocumentApiClient } from 'utils/api/documentApi'
import { ImageTypePattern } from 'utils/formValidation'

const useColumnDropDown = (props: GridCellProps) => {
  const handleChange = (event: { value: number }) => {
    if (props.onChange) {
      props.onChange({
        dataIndex: props.dataIndex,
        dataItem: props.dataItem,
        field: props.field,
        value: event,
        syntheticEvent: undefined as unknown as React.SyntheticEvent,
      })
    }
  }

  const data = props.dataItem
  const value = props?.field ? data[props.field] : null

  return {
    value: !!value && !!props?.field ? value.toString() : '',
    isDisabled: !data.inEdit && props.field,
    handleChange,
  }
}
export const ColumnTypeDropdown: React.FC<GridCellProps> = (props) => {
  const { isDisabled, handleChange } = useColumnDropDown(props)

  const documentTypesByCategory = useAppSelector((state) => state.documents.documentTypesByCategory)
  const categoryId = props.dataItem?.category?.value
  const currentType = props.dataItem?.type

  const getDocumentTypeData = (): { text: string; value: number }[] => {
    const types = categoryId ? documentTypesByCategory[categoryId] || [] : []
    return types.map((docType: ClientDocumentType) => ({
      text: docType.name,
      value: docType.id,
    }))
  }

  const options = getDocumentTypeData()
  const selectedOption = options.find((o) => o.value === currentType?.value)
  const value = selectedOption
    ? { value: selectedOption.value, label: selectedOption.text }
    : undefined

  if (isDisabled) {
    return <td>{currentType?.label || currentType?.value || ''}</td>
  }

  return (
    <td>
      <Select
        key={`${props.dataItem.uid}`}
        labelInValue
        value={value}
        onChange={handleChange}
        showSearch
        fullWidth
      >
        {options.map((data) => (
          <Option key={data.value} value={data.value}>
            {data.text}
          </Option>
        ))}
      </Select>
    </td>
  )
}

export const StatusTypeDropdown: React.FC<GridCellProps> = (props) => {
  const { handleChange } = useColumnDropDown(props)
  if (ImageTypePattern.test(props.dataItem.extension)) {
    return null
  }
  return (
    <td>
      <Select labelInValue={true} onChange={handleChange} showSearch fullWidth>
        {[
          { text: 'Not set', value: 0 },
          { text: 'Approved', value: 2 },
        ].map((data) => (
          <Option key={data.value} value={data.value}>
            {data.text}
          </Option>
        ))}
      </Select>
    </td>
  )
}

export const CountryTypeDropdown: React.FC<GridCellProps> = (props) => {
  const { isDisabled, handleChange } = useColumnDropDown(props)

  const countries = useAppSelector((state) => state.identity.countries)
  const currentCountry = props.dataItem?.country

  const options = (countries || []).map((country: CountryDto) => ({
    text: country.name,
    value: country.id,
  }))

  const selectedOption = options.find((o) => o.value === currentCountry?.value)
  const value = selectedOption
    ? { value: selectedOption.value, label: selectedOption.text }
    : undefined

  if (isDisabled) {
    return <td>{currentCountry?.label || currentCountry?.value || ''}</td>
  }

  return (
    <td>
      <Select labelInValue value={value} onChange={handleChange} showSearch allowClear fullWidth>
        {options.map((data) => (
          <Option key={data.value} value={data.value}>
            {data.text}
          </Option>
        ))}
      </Select>
    </td>
  )
}

export const ColumnCategoryDropdown: React.FC<GridCellProps> = (props) => {
  const documentApiClient = useApiClient(DocumentApiClient)
  const { isDisabled, handleChange } = useColumnDropDown(props)

  const categoryType = useAppSelector((state) => state.documents.categoryType)
  const dispatch = useAppDispatch()
  const currentCategory = props.dataItem?.category

  const getCategoryTypeData = (): { text: string; value: number }[] => {
    return categoryType.map((catType: CategoryType) => ({
      text: catType.name,
      value: catType.id,
    }))
  }

  const options = getCategoryTypeData()
  const selectedOption = options.find((o) => o.value === currentCategory?.value)
  const value = selectedOption
    ? { value: selectedOption.value, label: selectedOption.text }
    : undefined

  const updateDocumentType = (event: { value: number }) => {
    dispatch(getDocumentsTypesByCategory(event.value, documentApiClient))
  }

  if (isDisabled) {
    return <td>{currentCategory?.label || currentCategory?.value || ''}</td>
  }

  return (
    <td>
      <Select
        key={`${props.dataItem.uid}`}
        labelInValue
        value={value}
        onChange={(event) => {
          handleChange(event)
          updateDocumentType(event)
        }}
        showSearch
        fullWidth
      >
        {options.map((data) => (
          <Option key={data.value} value={data.value}>
            {data.text}
          </Option>
        ))}
      </Select>
    </td>
  )
}
